<template>
  <div class="form-sec form-sec--bundle-price">
    <MdTypography
      class="mt-e-title"
      data-qa-id="bundle-price-title"
      variant="heading7--regular"
      color="spirits--800"
      tag="h3"
    >
      {{ $t('pages.bundle.draft.bundle_price.title') }}
    </MdTypography>
    <MdTypography
      class="mt-e-para"
      data-qa-id="bundle-price-description"
      variant="body2--regular"
      color="spirits--700"
      tag="p"
    >
      {{ $t('pages.bundle.draft.bundle_price.desc') }}
    </MdTypography>
    <div>
      <div
        class="form-sec__content form-sec--bundle-price__enter-price is-flex is-flex--align-center is-flex--justify-between"
      >
        <div class="form-sec__content-left is-flex is-flex--align-center is-flex--justify-between flex-1">
          <div class="form-sec--bundle-price__font-details is-flex is-flex--align-end">
            <MdTypography
              v-if="bundleFormData?.name"
              v-tooltip="{
                content: bundleFormData.name,
                placement: 'bottom',
                disabled: bundleFormData.name.length <= BUNDLE_PRICE_CONSTANTS.max_truncate_string_length
              }"
              class="mt-e-title"
              data-qa-id="bundle-name"
              variant="heading6--medium"
              color="spirits--800"
              tag="h2"
            >
              {{ Misc.truncate(bundleFormData.name, BUNDLE_PRICE_CONSTANTS.max_truncate_string_length) }}
            </MdTypography>
            <template v-if="commaSeparatedFoundries">
              <MdTypography
                :class="$style['text-lowercase']"
                class="mt-e-para mt-e-para--seperator"
                data-qa-id="bundle-foundries"
                variant="body1--regular"
                color="spirits--700"
                tag="p"
              >
                {{ $t('pages.bundle.draft.by') }}
              </MdTypography>
              <MdTypography
                v-tooltip="{
                  content: commaSeparatedFoundries,
                  placement: 'bottom',
                  disabled: foundriesCount <= BUNDLE_PRICE_CONSTANTS.comma_separation_max_count
                }"
                class="mt-e-para mt-e-para--foundry-name"
                data-qa-id="bundle-foundries"
                variant="body1--regular"
                color="spirits--700"
                tag="p"
              >
                {{ selectedFoundries }}
              </MdTypography>
            </template>
          </div>
          <p class="mt-pill">
            <MdTypography
              variant="body2--regular"
              color="spirits--900"
              tag="span"
            >
              {{ fontsCount }} {{ $t('pages.bundle.draft.bundle_price.Fonts') }}
            </MdTypography>
          </p>
        </div>
        <div class="form-sec__content-right is-flex is-flex--align-center">
          <MdTypography
            variant="body2--medium"
            color="spirits--900"
            tag="span"
          >
            {{ $t('pages.bundle.draft.bundle_price.country_codes.USD') }}
            {{ $t('pages.bundle.draft.bundle_price.Price') }}
          </MdTypography>

          <div
            v-if="isCurrencyLoading"
            class="mt-spinner"
          >
            <MdSpinner
              color="blue"
              class="pageSpinner"
              data-qa-id="loading-icon"
            />
          </div>
          <div
            v-else
            class="mt-input"
            :data-currency-symbol="$t('pages.bundle.draft.bundle_price.$')"
            :class="[$style.currency, { 'mt-input--error': Object.hasOwn(errors, usdPriceField) }]"
          >
            <TextField
              v-model="usdPrice"
              data-qa-id="usdPriceInput"
              :name="usdPriceField"
              :input-class="[$style.currencyInput]"
              :placeholder="placeHolderUSDPrice"
              rules="fou_numbers|required|decimal:2|min_value:1|max_decimal:10000"
              :error-class="$style.errorField"
              @update:model-value="usdPriceUpdateModelValue"
              @blur="e => onUSDPriceBlur({ price: e.target.value, currency: BUNDLE_PRICE_CONSTANTS.USD })"
            >
              <template #errorContent>
                <TooltipError :errors="BUNDLE_PRICE_CONSTANTS.prices_usd_validation_messages" />
              </template>
            </TextField>
          </div>
        </div>
      </div>
      <div class="form-sec--bundle-price__conversion-price">
        <MdTypography
          class="mt-e-title"
          variant="body1--regular"
          color="spirits--800"
          tag="h4"
        >
          {{ $t('pages.bundle.draft.bundle_price.price_in_other') }}
        </MdTypography>
        <MdTypography
          class="mt-e-para"
          variant="body2--regular"
          color="spirits--700"
          tag="p"
        >
          {{ $t('pages.bundle.draft.bundle_price.price_in_other_desc') }}
        </MdTypography>
  
        <table class="mt-table">
          <tr class="mt-table__row">
            <th
              v-for="(countryCode, i) in countryCodes"
              :key="i"
              class="mt-table__header"
            >
              {{ $t(`pages.bundle.draft.bundle_price.country_codes.${countryCode.acronym}`) }}
            </th>
          </tr>
          <tbody class="mt-table__tbody">
            <tr class="mt-table__row">
              <td
                v-for="(countryCode, i) in countryCodes"
                :key="i"
                class="mt-table__content"
               >
                <div
                  v-if="isCurrencyLoading"
                  class="mt-spinner"
                >
                  <MdSpinner
                    color="blue"
                    class="pageSpinner"
                    data-qa-id="loading-icon"
                  />
                </div>
                <div
                  v-else
                  class="mt-input"
                  :data-currency-symbol="countryCode.symbol"
                  :class="[$style.currency , { 'is-error': Object.hasOwn(errors, countryCode.acronym) }]"
                >
                  <TextField
                    v-model="countryCode.price"
                    :name="countryCode.acronym"
                    :placeholder="convertedPrice[countryCode.acronym]"
                    rules="fou_numbers|decimal:2|min_value:1|max_decimal:999999"
                    :input-class="[$style.currencyInput]"
                    @blur="e => onUSDPriceBlur({ price: e.target.value, currency: countryCode.acronym })"
                  >
                    <template #errorContent>
                      <TooltipError :errors="BUNDLE_PRICE_CONSTANTS.prices_validation_messages" />
                    </template>
                  </TextField>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <MdTypography
        v-if="priceWarning.show"
        variant="body2--regular"
        color="taco-yellow--500"
        tag="p"
        class="mt-warn mt-20"
        data-qa-id="price-warning"
        v-html="priceWarning.message"
      />
    </div>
  </div>
</template>

<script src="./script/index.js" />

<style lang="scss" module>
// FOU CSS Component: submissions/prices
@import 'mpl-css/lib/assets/vendor/include-media/index';
@import 'mpl-css/lib/assets/global/variables/breakpoints';
@import 'mpl-css/lib/assets/global/variables/colors';
@import 'mpl-css/lib/assets/global/variables/fonts';
@import 'mpl-css/lib/assets/global/variables/grid';
@import 'mpl-css/lib/assets/global/variables/layout';
@import 'mpl-css/lib/assets/global/functions/index';

// FOU Dependencies
@import 'objects/serif/index';
@import 'objects/link/index';

@import 'src/pages/submissions/components/prices/stylesheets/currencies.scss';
.errorField {
  font-size: rem(13);
  line-height: rem(16);
}

.text-lowercase {
  text-transform: lowercase;
}
</style>

<style lang="scss">
@import 'mpl-css/lib/assets/global/variables/fonts';
@import 'mpl-css/lib/assets/global/variables/colors';
@import 'mpl-css/lib/assets/global/variables/breakpoints';
@import 'mpl-css/lib/assets/global/functions/index';

.form-sec {
  &--bundle-price {
    .form-sec--bundle-price__enter-price {
      border: rem(1) solid map-get($colors, 'spirits--300');
      border-radius: rem(16);
      margin-bottom: rem(32);
      margin-top: rem(32);

      @media screen and (max-width: 1279px) {
        flex-direction: column;
        margin-bottom: rem(24);
        margin-top: rem(24);
      }
    }

    &.form-sec--bundle-price .mt-e-para {
      margin-bottom: 0;
    }

    .mt-spinner {
      .pageSpinner {
        margin-bottom: 0;
        width: rem(24);
      }
    }

    .form-sec__content-left {
      width: calc(100% - 252px);
      padding: rem(24);

      @media screen and (max-width: 1279px) {
        width: 100%;
      }

      @media screen and (max-width: 719px) {
        flex-direction: column;
        gap: rem(16);
        align-items: flex-start;
      }

      .form-sec--bundle-price__font-details {
        @media screen and (max-width: 719px) {
          flex-wrap: wrap;
          align-items: flex-start;
          width: 100%;
        }

        .mt-e-title {
          font-family: $mt-regular;
          margin: 0 rem(16) 0 0;
          font-size: rem(23);
          line-height: rem(32);
          word-break: break-all;

          @media screen and (max-width: 719px) {
            width: 100%;
          }
        }

        .mt-e-para {
          margin-bottom: 0;
          font-size: rem(16);
          line-height: rem(24);
          letter-spacing: rem(-0.47);

          &--seperator {
            margin-right: rem(4);
          }
        }
      }

      .mt-pill {
        background-color: map-get($colors, 'spirits--100');
        font-size: rem(13);
        line-height: rem(16);
        border-radius: rem(8);
        padding: rem(12) rem(16);
        color: #1e242c;

        @media screen and (max-width: 719px) {
          width: 100%;
          text-align: center;
        }
      }
    }

    .form-sec__content-right {
      padding: rem(24);
      gap: rem(16);
      border-left: 1px solid map-get($colors, 'spirits--300');

      @media screen and (max-width: 1279px) {
        width: 100%;
        border-left: none;
        border-top: 1px solid map-get($colors, 'spirits--300');
      }

      .mt-label {
        font-size: rem(13);
        line-height: rem(16);
        color: map-get($colors, 'spirits--900');
        text-transform: none;
        font-family: $mt-medium;
        font-weight: 500;

        @media screen and (max-width: 1279px) {
          width: rem(79);
        }
      }

      & > .mt-input {
        width: rem(125);
        height: rem(56);
        border-radius: rem(8);
        border: 1px solid map-get($colors, 'spirits--300');
        align-items: center;
        padding: rem(16);
        position: relative;

        @media screen and (max-width: 1279px) {
          width: calc(100% - 79px);
          height: rem(40);
        }

        &--error {
          border: 1px solid map-get($colors, 'red-babe--500');
        }

        &::before {
          padding-left: 0;
        }

        .mt-error-wrapper {
          position: absolute;
          top: 100%;
          font-size: rem(11);
          left: 0;
          right: 0;

          @media screen and (max-width: 1279px) {
            top: 50%;
            margin-top: 0;
            left: unset;
            transform: translateY(-50%);
            right: rem(12);
          }

          @media screen and (max-width: 719px) {
            font-size: 0;
          }
        }
      }

      .mt-spinner {
        display: inline-flex;
        height: rem(56);

        .pageSpinner {
          margin-bottom: rem(4);
        }
      }
    }

    .form-sec--bundle-price__conversion-price {
      td,
      th {
        border: 1px solid #dddddd;
        text-align: left;
        padding: rem(8);
      }

      .mt-table {
        width: 100%;
      }

      .mt-e-title {
        font-size: rem(16);
        font-family: $mt-regular;
        margin-bottom: rem(8);
        color: map-get($colors, 'spirits--800');
        letter-spacing: rem(-0.47);

        @media screen and (max-width: 1279px) {
          display: none;
        }
      }

      .mt-e-para {
        margin-bottom: rem(16);

        @media screen and (max-width: 1279px) {
          display: none;
        }
      }

      .mt-table {
        border-radius: rem(16);
        border: 1px solid map-get($colors, 'spirits--300');
        border-collapse: unset;

        @media screen and (max-width: 1279px) {
          display: inline-flex;
          width: 100%;
        }

        &__row {
          @media screen and (max-width: 1279px) {
            display: inline-flex;
            width: rem(116);
            flex-direction: column;

            & > :nth-child(odd) {
              background-color: map-get($colors, 'spirits--50');
            }
          }

          .mt-table__header {
            padding: 0 rem(16);
            font-size: rem(13);
            line-height: rem(16);
            color: map-get($colors, 'spirits--700');
            font-weight: 500;
            border-width: 0 0 1px 0;
            border-color: map-get($colors, 'spirits--300');
            height: rem(48);
            vertical-align: middle;

            &:not(:first-child) {
              border-left-width: 1px;
            }

            @media screen and (max-width: 1279px) {
              display: inline-flex;
              align-items: center;
              border-bottom-width: 0;

              &:not(:first-child) {
                border-left-width: 0;
                border-top-width: 1px;
              }

              &:first-child {
                border-top-left-radius: rem(16);
              }

              &:last-child {
                border-bottom-left-radius: rem(16);
              }
            }
          }

          .mt-table__content {
            border: 0;
            padding: 0 rem(16);
            font-size: rem(11);
            line-height: rem(16);
            height: rem(48);
            vertical-align: middle;
            position: relative;

            &:not(:first-child) {
              border-left: 1px solid map-get($colors, 'spirits--300');
            }

            @media screen and (max-width: 1279px) {
              display: inline-flex;
              align-items: center;
              border-left: 1px solid map-get($colors, 'spirits--300');

              &:not(:first-child) {
                border-top: 1px solid map-get($colors, 'spirits--300');
              }

              &:first-child {
                border-top-right-radius: rem(16);
              }

              &:last-child {
                border-bottom-right-radius: rem(16);
              }
            }

            & > .mt-input {
              border: 1px solid map-get($colors, 'spirits-light');
              padding: 0;
              align-items: center;
              position: unset;
              padding-left: 0.2rem;

              @media screen and (max-width: 1279px) {
                width: 100%;
                background-color: transparent;
              }

              .mt-input {
                width: 100%;
              }

              &::before {
                padding-left: 0;
                font-size: rem(11);
                color: map-get($colors, 'spirits--700');
              }

              ::placeholder {
                color: map-get($colors, 'spirits--700');
                opacity: 1;
                font-size: rem(11);
              }

              .mt-error-wrapper {
                position: absolute;
                top: 100%;
                margin-top: rem(4);
                margin-left: 1.125rem;
                left: 0;

                @media screen and (max-width: 1279px) {
                  top: 50%;
                  margin-top: 0;
                  margin-left: 0;
                  left: unset;
                  transform: translateY(-50%);
                  right: rem(19);
                }

                @media screen and (max-width: 719px) {
                  font-size: 0;
                }
              }
            }

            .is-error {
              border: 1px solid map-get($colors, 'red-babe--500');

              & + .mt-table__content.is-error {
                border-left-width: 0;
              }

              @media screen and (max-width: 1279px) {

                & + .mt-table__content.is-error {
                  border-top: 0;
                  border-left-width: rem(1);
                }
              }
            }
          }
        }

        &__tbody {
          @media screen and (max-width: 1279px) {
            width: calc(100% - 116px);

            .mt-table__row {
              width: 100%;
            }
          }
        }
      }
    }
  }
}

@media screen and (max-width: 719px) {
  .is-create-bundle-draft {
    .v-popper--theme-tooltip .v-popper__inner {
      border-top-right-radius: rem(2);
      border-bottom-right-radius: rem(2);
    }
  }
}
</style>
